import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { OtpService } from '../../services/otp/otp.service';
import { ROUTE_PATHS } from '../../utils/route-paths.const';
import { OtpRouteType } from '../../enums/otp-verification-types.enum';

/**
 * OTP Guard
 * Handles two scenarios based on route data:
 * 1. OTP Entry (requireOtpToken: false) - Checks phone + OTP ID + valid route type
 * 2. OTP Token (requireOtpToken: true) - Checks phone + OTP token
 *
 * Usage in routes:
 * - For OTP entry page: data: { requireOtpToken: false }
 * - For protected pages after OTP: data: { requireOtpToken: true } or no data (defaults to true)
 */
export const otpGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const otpService = inject(OtpService);
  const router = inject(Router);

  const phoneNumber = otpService.getPhoneNumber();
  const requireOtpToken = route.data['requireOtpToken'] ?? true;

  if (requireOtpToken) {
    // Check for OTP token (user completed verification) - for protected pages
    const otpToken = otpService.getOtpToken();

    if (phoneNumber && otpToken) {
      return true;
    }
  } else {
    // Check for OTP ID (user can enter code) - for OTP verification page
    const otpId = otpService.getOtpId();
    const type = route.paramMap.get('type');
    const isValidType = Object.values(OtpRouteType).includes(
      type as OtpRouteType
    );

    if (phoneNumber && otpId && isValidType) {
      return true;
    }
  }

  // Clear any partial OTP data and redirect to phone verification
  // Keep phone number for better UX when user navigates back
  otpService.clearOtpId();
  otpService.clearOtpToken();
  router.navigate([ROUTE_PATHS.AUTH, ROUTE_PATHS.PHONE_VERIFICATION], {
    replaceUrl: true,
  });
  return false;
};
